import React, { ReactNode, useEffect, useState } from 'react'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import { useResource } from '../resource/useResource'
import type { PAMSubject } from '../api/types'
import type {
  AdminCreateSubjectReq,
  AdminSubjectItem,
  AdminUpdateSubjectReq,
  AdminUserActiveThreatHistoryPoint,
  AdminUserRiskHistoryPoint,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { UserSecurityHistoryChart } from '../components/UserSecurityHistoryChart'

const TYPE_OPTIONS = [
  'human',
  'admin',
  'vendor',
  'service',
  'workload',
  'ci_cd',
  'bot',
  'break_glass',
].map((v) => ({ value: v, label: v }))

const LIFECYCLE_OPTIONS = ['active', 'disabled', 'dormant'].map((v) => ({
  value: v,
  label: v,
}))

const ASSURANCE_OPTIONS = ['', 'aal1', 'aal2', 'aal3'].map((v) => ({
  value: v,
  label: v || '(none)',
}))

const RISK_OPTIONS = ['low', 'medium', 'high', 'critical'].map((v) => ({
  value: v,
  label: v,
}))

const ACTIVE_THREAT_OPTIONS = ['none', 'low', 'medium', 'high', 'critical'].map(
  (v) => ({ value: v, label: v })
)

type SecurityHistoryState = {
  subjectID: string
  riskPoints: AdminUserRiskHistoryPoint[]
  activeThreatPoints: AdminUserActiveThreatHistoryPoint[]
  loading: boolean
  error: string | null
  nextCursor: string
  loadingMore: boolean
  rangeFrom: string
  rangeTo: string
}

const columns: CrudColumn<PAMSubject>[] = [
  { key: 'display_name', header: 'Name' },
  { key: 'type', header: 'Type' },
  { key: 'lifecycle', header: 'Lifecycle' },
  {
    key: 'assurance_level',
    header: 'AAL',
    render: (s) => s.assurance_level ?? '-',
  },
  { key: 'risk_level', header: 'Risk' },
  { key: 'active_threat_level', header: 'Active threat' },
  {
    key: 'pam_access_disabled',
    header: 'PAM',
    render: (s) => (s.pam_access_disabled ? 'Disabled' : 'Allowed'),
  },
  {
    key: 'privilege_escalation_disabled',
    header: 'Privilege',
    render: (s) => (s.privilege_escalation_disabled ? 'Disabled' : 'Allowed'),
  },
]

const fields: CrudField[] = [
  { name: 'display_name', label: 'Display Name', type: 'text' },
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    options: TYPE_OPTIONS,
    required: true,
  },
  {
    name: 'lifecycle',
    label: 'Lifecycle',
    type: 'select',
    options: LIFECYCLE_OPTIONS,
  },
  {
    name: 'assurance_level',
    label: 'Assurance Level',
    type: 'select',
    options: ASSURANCE_OPTIONS,
  },
  {
    name: 'risk_level',
    label: 'Risk Level',
    type: 'select',
    options: RISK_OPTIONS,
  },
  {
    name: 'active_threat_level',
    label: 'Active Threat Level',
    type: 'select',
    options: ACTIVE_THREAT_OPTIONS,
  },
  {
    name: 'pam_access_disabled',
    label: 'Disable PAM access',
    type: 'checkbox',
  },
  {
    name: 'privilege_escalation_disabled',
    label: 'Disable privilege escalation',
    type: 'checkbox',
  },
  { name: 'linked_user_id', label: 'Linked User ID', type: 'text' },
  {
    name: 'risk_context',
    label: 'Risk Context (JSON, write-only)',
    type: 'textarea',
  },
]

const CREATE_INITIAL: Record<string, unknown> = {
  type: 'human',
  lifecycle: 'active',
  risk_level: 'low',
  active_threat_level: 'none',
  pam_access_disabled: false,
  privilege_escalation_disabled: false,
}

/**
 * Map the generated AdminSubjectItem (camelCase) → PAMSubject (snake_case)
 * so all existing column/form/edit logic stays unchanged. Was previously
 * typed against an inline structural type identical to AdminSubjectItem;
 * switched to import the real generated type (OIDCPAM Gate-6 Phase 4b hygiene
 * fix, matching the Phase 4a Resources.tsx precedent) so a future codegen
 * field rename fails typecheck here instead of silently drifting.
 */
function toPAMSubject(s: AdminSubjectItem): PAMSubject {
  return {
    id: s.id,
    tenant_id: s.tenantId,
    type: s.type,
    lifecycle: s.lifecycle,
    display_name: s.displayName ?? '',
    assurance_level: s.assuranceLevel,
    linked_user_id: s.linkedUserId,
    risk_level: s.riskLevel,
    active_threat_level: s.activeThreatLevel,
    pam_access_disabled: s.pamAccessDisabled,
    privilege_escalation_disabled: s.privilegeEscalationDisabled,
    external_ids: s.externalIds ?? [],
    created_at: s.createdAt,
    updated_at: s.updatedAt,
  }
}

const toCreateReq = (
  tenantId: string,
  v: Record<string, unknown>
): AdminCreateSubjectReq => ({
  tenantId,
  type: String(v.type || 'human'),
  displayName: v.display_name ? String(v.display_name) : undefined,
  lifecycle: v.lifecycle ? String(v.lifecycle) : undefined,
  assuranceLevel: v.assurance_level ? String(v.assurance_level) : undefined,
  linkedUserId: v.linked_user_id ? String(v.linked_user_id) : undefined,
  riskLevel: String(v.risk_level || 'low'),
  activeThreatLevel: String(v.active_threat_level || 'none'),
  pamAccessDisabled: Boolean(v.pam_access_disabled),
  privilegeEscalationDisabled: Boolean(v.privilege_escalation_disabled),
  riskContext: v.risk_context ? String(v.risk_context) : undefined,
})

const toUpdateReq = (
  tenantId: string,
  subjectId: string,
  v: Record<string, unknown>
): AdminUpdateSubjectReq => ({
  tenantId,
  subjectId,
  type: v.type ? String(v.type) : undefined,
  displayName: v.display_name ? String(v.display_name) : undefined,
  lifecycle: v.lifecycle ? String(v.lifecycle) : undefined,
  assuranceLevel: v.assurance_level ? String(v.assurance_level) : undefined,
  linkedUserId: v.linked_user_id ? String(v.linked_user_id) : undefined,
  riskLevel: v.risk_level ? String(v.risk_level) : undefined,
  activeThreatLevel: v.active_threat_level
    ? String(v.active_threat_level)
    : undefined,
  pamAccessDisabled: Boolean(v.pam_access_disabled),
  privilegeEscalationDisabled: Boolean(v.privilege_escalation_disabled),
  riskContext: v.risk_context ? String(v.risk_context) : undefined,
})

export const Subjects = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [editing, setEditing] = useState<PAMSubject | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PAMSubject | null>(null)
  const [historySubject, setHistorySubject] = useState<PAMSubject | null>(null)
  const [historyReload, setHistoryReload] = useState(0)
  const [history, setHistory] = useState<SecurityHistoryState>({
    subjectID: '',
    riskPoints: [],
    activeThreatPoints: [],
    loading: false,
    error: null,
    nextCursor: '',
    loadingMore: false,
    rangeFrom: '',
    rangeTo: '',
  })

  useEffect(() => {
    if (!historySubject) return undefined
    let active = true
    const rangeTo = new Date().toISOString()
    const rangeFrom = new Date(
      Date.parse(rangeTo) - 30 * 24 * 60 * 60 * 1000
    ).toISOString()
    setHistory({
      subjectID: historySubject.id,
      riskPoints: [],
      activeThreatPoints: [],
      loading: true,
      error: null,
      nextCursor: '',
      loadingMore: false,
      rangeFrom,
      rangeTo,
    })
    client
      .adminGetSubjectSecurityHistory({
        tenantId: tenant,
        subjectId: historySubject.id,
        from: rangeFrom,
        to: rangeTo,
      })
      .then((response) => {
        if (!active) return
        setHistory({
          subjectID: historySubject.id,
          riskPoints: response.riskPoints ?? [],
          activeThreatPoints: response.activeThreatPoints ?? [],
          loading: false,
          error: null,
          nextCursor: response.nextCursor ?? '',
          loadingMore: false,
          rangeFrom,
          rangeTo,
        })
      })
      .catch((error: unknown) => {
        if (!active) return
        setHistory({
          subjectID: historySubject.id,
          riskPoints: [],
          activeThreatPoints: [],
          loading: false,
          error:
            error instanceof Error ? error.message : 'History request failed',
          nextCursor: '',
          loadingMore: false,
          rangeFrom,
          rangeTo,
        })
      })
    return () => {
      active = false
    }
  }, [client, historyReload, historySubject, tenant])

  const handleLoadOlderHistory = (): void => {
    if (!historySubject || !history.nextCursor || history.loadingMore) return
    const subjectID = historySubject.id
    const cursor = history.nextCursor
    const rangeFrom = history.rangeFrom
    const rangeTo = history.rangeTo
    setHistory((current) => ({ ...current, loadingMore: true, error: null }))
    client
      .adminGetSubjectSecurityHistory({
        tenantId: tenant,
        subjectId: subjectID,
        cursor,
        from: rangeFrom,
        to: rangeTo,
      })
      .then((response) => {
        setHistory((current) => {
          if (
            current.subjectID !== subjectID ||
            current.rangeFrom !== rangeFrom ||
            current.rangeTo !== rangeTo
          ) {
            return current
          }
          return {
            ...current,
            riskPoints: [...current.riskPoints, ...(response.riskPoints ?? [])],
            activeThreatPoints: [
              ...current.activeThreatPoints,
              ...(response.activeThreatPoints ?? []),
            ],
            nextCursor: response.nextCursor ?? '',
            loadingMore: false,
          }
        })
      })
      .catch((error: unknown) => {
        setHistory((current) => {
          if (
            current.subjectID !== subjectID ||
            current.rangeFrom !== rangeFrom ||
            current.rangeTo !== rangeTo
          ) {
            return current
          }
          return {
            ...current,
            loadingMore: false,
            error:
              error instanceof Error ? error.message : 'History request failed',
          }
        })
      })
  }

  const rs = useResource<PAMSubject>({
    list: async () => {
      const resp = await client.adminListSubjects({ tenantId: tenant })
      return (resp.subjects ?? []).map(toPAMSubject)
    },
    remove: async (id) => {
      await client.adminDeleteSubject({ tenantId: tenant, subjectId: id })
    },
  })

  const handleCreate = async (v: Record<string, unknown>): Promise<void> => {
    try {
      await client.adminCreateSubject(toCreateReq(tenant, v))
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleEdit = async (v: Record<string, unknown>): Promise<void> => {
    if (!editing) return
    try {
      await client.adminUpdateSubject(toUpdateReq(tenant, editing.id, v))
      await rs.reload()
      setEditing(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (s: PAMSubject): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditing(s)
          setActionError(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudSecondaryButton
        type="button"
        aria-label={`View security history for ${s.display_name || s.id}`}
        onClick={() => setHistorySubject(s)}
      >
        Security History
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(s)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>
          Please select a tenant to manage subjects.
        </TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>Subjects</PageTitle>

      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}

      <SectionBlock>
        <SectionTitle>All Subjects</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Subjects list"
          emptyMessage="No subjects configured yet."
        />
      </SectionBlock>

      {historySubject && (
        <SectionBlock aria-live="polite">
          <SectionTitle>
            Security history —{' '}
            {historySubject.display_name || historySubject.id}
          </SectionTitle>
          <CrudSecondaryButton
            type="button"
            onClick={() => setHistorySubject(null)}
          >
            Close
          </CrudSecondaryButton>
          <UserSecurityHistoryChart
            riskPoints={history.riskPoints}
            activeThreatPoints={history.activeThreatPoints}
            loading={history.loading}
            error={history.error}
            onRetry={() => setHistoryReload((value) => value + 1)}
          />
          {history.nextCursor && !history.error && (
            <CrudSecondaryButton
              type="button"
              disabled={history.loadingMore}
              onClick={handleLoadOlderHistory}
            >
              {history.loadingMore ? 'Loading…' : 'Load older history'}
            </CrudSecondaryButton>
          )}
        </SectionBlock>
      )}

      {editing && (
        <SectionBlock>
          <SectionTitle>Edit Subject</SectionTitle>
          <CrudForm
            key={editing.id}
            fields={fields}
            initial={{
              display_name: editing.display_name,
              type: editing.type,
              lifecycle: editing.lifecycle,
              assurance_level: editing.assurance_level ?? '',
              linked_user_id: editing.linked_user_id ?? '',
              risk_level: editing.risk_level,
              active_threat_level: editing.active_threat_level,
              pam_access_disabled: editing.pam_access_disabled,
              privilege_escalation_disabled:
                editing.privilege_escalation_disabled,
              risk_context: '',
            }}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditing(null)
              setActionError(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionTitle>Create Subject</SectionTitle>
        <CrudForm
          fields={fields}
          initial={CREATE_INITIAL}
          onSubmit={handleCreate}
          submitLabel="Create Subject"
        />
      </SectionBlock>

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete subject"
        body={`Delete subject "${pendingDelete?.display_name ?? pendingDelete?.id ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
